import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const pyramidLayers = [
  {
    level: '第三层',
    title: '分档稳定性',
    desc: '全量三档单调率约96%，四档约76%，五档约38%',
    detail: '三档诊断比过细排名更加稳妥',
    icon: 'ri-stack-line',
  },
  {
    level: '第二层',
    title: '排序一致性',
    desc: '专用模型对单专家金标ρ约0.21；本科感染力轨对专家面板约0.632、对学生面板约0.588',
    detail: '内容轨、感染力轨分别捕捉不同信号，融合结果进一步接近综合金标',
    icon: 'ri-sort-asc',
  },
  {
    level: '第一层',
    title: '金标信度',
    desc: '单专家信度约0.03—0.06，AI一致性上限约0.10—0.22',
    detail: 'AI要对齐人工，首先要知道人工金标能提供多高的一致性上限',
    icon: 'ri-shield-check-line',
  },
];

const robustnessItems = [
  { label: '中心化验证', value: '四组排序相关均为 1.000', desc: '将每位评委宽严偏好中心化后，逐课排序完全不变' },
  { label: '标准化验证', value: '专家与学生面板仍保持较高相关', desc: '高职学生组在更激进标准化下波动较大，原因为样本量小' },
];

export default function P43CredibilityVerify({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-14">
        <div ref={titleRef} className="text-center mb-6 md:mb-8">
          <span className="inline-block text-xs font-body font-semibold text-accent-600 uppercase tracking-[0.15em] mb-2 transition-all duration-700">验证体系</span>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 transition-all duration-1000 delay-100">
            可信度验证：<span className="text-primary-600">三层体系</span>
          </h2>
          <p className="text-sm text-foreground-500 font-body mt-2 max-w-lg mx-auto">把分散证据收成三层体系：金标信度→排序一致性→分档稳定性</p>
        </div>

        {/* 三层金字塔 */}
        <div className="flex flex-col items-center gap-4 md:gap-5 max-w-4xl mx-auto mb-6 md:mb-8 transition-all duration-1000 delay-200">
          {pyramidLayers.map((layer, i) => {
            const widths = ['w-full max-w-xl', 'w-full max-w-2xl', 'w-full max-w-3xl'];
            return (
              <div key={layer.level} className={`${widths[i]} p-4 md:p-5 rounded-xl ${i % 2 === 0 ? 'glass-liquid border-primary-200/30' : 'glass-liquid-gold border-accent-200/30'} transition-all duration-500 hover:scale-[1.01]`} style={{ transitionDelay: `${200 + i * 120}ms` }}>
                <div className="relative z-10">
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${i % 2 === 0 ? 'glass-blue border-primary-200/40' : 'glass-gold border-accent-200/40'}`}>
                      <i className={`${layer.icon} text-base ${i % 2 === 0 ? 'text-primary-600' : 'text-accent-600'}`} />
                    </div>
                    <div>
                      <span className="text-[10px] font-body font-semibold text-foreground-400 leading-none">{layer.level}</span>
                      <h3 className="font-heading text-base font-bold text-foreground-800 leading-tight">{layer.title}</h3>
                    </div>
                  </div>
                  <p className="text-xs text-foreground-600 font-body leading-relaxed mb-1">{layer.desc}</p>
                  <p className="text-xs text-foreground-400 font-body leading-relaxed">{layer.detail}</p>
                </div>
              </div>
            );
          })}
        </div>

        {/* 稳健性验证 */}
        <div className="max-w-3xl mx-auto transition-all duration-1000 delay-600">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
            {robustnessItems.map((item) => (
              <div key={item.label} className="p-4 md:p-5 rounded-xl glass border-white/50">
                <p className="text-sm font-body font-bold text-foreground-800 mb-1">{item.label}</p>
                <p className="text-sm font-mono text-primary-700 font-bold mb-1">{item.value}</p>
                <p className="text-xs text-foreground-400 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}